import { Components, Theme } from '@mui/material';

export const cssBaseline = (theme: Theme): Components => ({
  MuiCssBaseline: {
    styleOverrides: {
      // WOFF2 first, TTF as a fallback for browsers that predate WOFF2
      // support (~2016). WOFF2 cuts these three fonts 60-73% smaller than the
      // original TTFs (182KB -> 70KB average) — see public/fonts/*.woff2.
      // These two families are also preloaded from _document.tsx, since
      // @font-face declared inside CSS-in-JS is only discovered once that
      // stylesheet parses, which was adding ~1.2s to the critical request
      // chain before the browser even started downloading the font.
      '@font-face': {
        fontFamily: 'co-head-line-corp-regular',
        fontDisplay: 'swap',
        fontStyle: 'normal',
        src: `local('co-head-line-corp-regular'),
            url('/fonts/co-head-line-trial-regular.woff2') format('woff2'),
            url('/fonts/co-head-line-trial-regular.ttf') format('truetype')`,
      },
      fallbacks: [
        {
          '@font-face': {
            fontFamily: 'co-head-line-corp-Light',
            fontDisplay: 'swap',
            fontStyle: 'normal',
            src: `local('co-head-line-corp-Light'),
          url('/fonts/co-head-line-corp-light.woff2') format('woff2'),
          url('/fonts/co-head-line-corp-light.ttf') format('truetype')`,
          },
        },
        {
          '@font-face': {
            fontFamily: 'co-head-line-corp-bold',
            fontDisplay: 'swap',
            fontStyle: 'normal',
            src: `local('co-head-line-corp-bold'),
          url('/fonts/co-head-line-trial-bold.woff2') format('woff2'),
          url('/fonts/co-head-line-trial-bold.ttf') format('truetype')`,
          },
        },
      ],
      '*': {
        margin: 0,
        padding: 0,
        boxSizing: 'border-box',
      },
      // '::-webkit-scrollbar': {
      //   width: '16px',
      //   height: '16px',
      //   backgroundColor: 'transparent',
      //   scrollBehavior: 'smooth',
      // },
      // '::-webkit-scrollbar-track': {
      //   borderRadius: '13px',
      //   backgroundColor: theme.palette.grey[10],
      // },
      // '::-webkit-scrollbar-thumb': {
      //   borderRadius: '13px',
      //   border: '4px solid transparent',
      //   backgroundClip: 'content-box',
      //   backgroundColor: theme.palette.grey[30],
      //   '&:hover': {
      //     backgroundColor: theme.palette.grey[40],
      //   },
      // },
      html: {
        width: '100%',
        height: '100%',
        fontSize: '11px',
        [theme.breakpoints.up('mediumDesktop')]: {
          fontSize: '12px',
        },
        [theme.breakpoints.up('largeDesktop')]: {
          fontSize: '13px',
        },
        [theme.breakpoints.up('extraLargeDesktop')]: {
          fontSize: '16px',
        },
      },
      body: {
        width: '100%',
        height: '100%',
      },
      '#root': {
        width: '100%',
        height: '100%',
        overflow: 'hidden',
        caretColor: theme.palette.primary.main,
      },
      input: {
        '&[type=number]': {
          MozAppearance: 'textfield',
          '&::-webkit-outer-spin-button': {
            margin: 0,
            WebkitAppearance: 'none',
          },
          '&::-webkit-inner-spin-button': {
            margin: 0,
            WebkitAppearance: 'none',
          },
        },
      },
      textarea: {
        '&::-webkit-input-placeholder': {
          color: theme.palette.text.disabled,
        },
        '&::-moz-placeholder': {
          opacity: 1,
          color: theme.palette.text.disabled,
        },
        '&:-ms-input-placeholder': {
          color: theme.palette.text.disabled,
        },
        '&::placeholder': {
          color: theme.palette.text.disabled,
        },
      },
      // mui-tel-input v9 renders country flags as an <img> (width 26 from
      // flagcdn), not the inline SVG of the old material-ui-phone-number, so
      // the per-field '& svg' sizing no longer applies — size it globally to
      // match the ~24px-wide flag shown on production.
      '.MuiTelInput-FlagImg': {
        width: '24px',
        height: 'auto',
      },
      //linear NProgress
      '#nprogress': {
        /* Make clicks pass-through */
        pointerEvents: 'none',
        '& .bar': {
          background: theme.palette.secondary.main,
          position: 'fixed',
          zIndex: 1502,
          top: 0,
          left: 0,
          width: '100%',
          height: '2px',
        },
        /* Fancy blur effect */
        '& .peg': {
          display: 'block',
          position: 'absolute',
          right: 0,
          width: '100px',
          height: '100%',
          opacity: 1,
          WebkitTransform: 'rotate(3deg) translate(0px, -4px)',
          msTransform: 'rotate(3deg) translate(0px, -4px)',
          transform: 'rotate(3deg) translate(0px, -4px)',
        },
        /* Remove these to get rid of the spinner */
        '& .spinner': {
          display: 'block',
          position: 'fixed',
          zIndex: 1101,
          top: '15px',
          right: '15px',
        },
        '&. spinner-icon': {
          width: '18px',
          height: '18px',
          boxSizing: 'border-box',
          border: 'solid 2px transparent',
          borderTopColor: theme.palette.secondary.main,
          borderLeftColor: theme.palette.secondary.main,
          borderRadius: '50%',
          WebkitAnimation: 'nprogress-spinner 400ms linear infinite',
          animation: 'nprogress-spinner 400ms linear infinite',
        },
      },
      '& .nprogress-custom-parent': {
        overflow: 'hidden',
        position: 'relative',
        '#nprogress': {
          '& .spinner': {
            position: 'absolute',
          },
          '& .bar': {
            position: 'absolute',
          },
        },
      },
      '@media (min-width: 640px)': {
        '#nprogress': {
          '& .bar': {
            height: '0.25rem',
          },
        },
      },
      '@-webkit-keyframes nprogress-spinner': {
        '0%': {
          WebkitTransform: 'rotate(0deg)',
        },
        '100%': {
          WebkitTransform: 'rotate(360deg)',
        },
      },
      '@keyframes nprogress-spinner': {
        '0%': {
          transform: 'rotate(0deg)',
        },
        '100%': {
          transform: 'rotate(360deg)',
        },
      },
    },
  },
});
